import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Truck, 
  RefreshCw, 
  Link2, 
  Unlink, 
  Loader2,
  Gauge,
  MapPin,
  Fuel,
  Clock,
  Check,
  AlertCircle,
  Settings,
  Download,
  Link,
  Info,
  Edit3,
  Save
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function GeliosIntegration() {
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [units, setUnits] = useState([]);
  const [buses, setBuses] = useState([]);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [selectedMappings, setSelectedMappings] = useState({}); // gelios_unit_id -> bus_id
  
  // For odometer editing
  const [editingOdometer, setEditingOdometer] = useState(null); // { unitId, busId, currentValue }
  const [odometerValue, setOdometerValue] = useState('');

  // Fetch settings and buses
  useEffect(() => {
    fetchSettings();
    fetchBuses();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/gelios/settings`, getAuthHeaders());
      setSettings(response.data);
      if (response.data.username) {
        setCredentials(prev => ({ ...prev, username: response.data.username }));
      }
    } catch (error) {
      console.error('Error fetching Gelios settings:', error);
    }
  };

  const fetchBuses = async () => {
    try {
      const response = await axios.get(`${API}/api/buses`, getAuthHeaders());
      setBuses(response.data.filter(b => !b.is_deleted));
    } catch (error) {
      console.error('Error fetching buses:', error);
    }
  };

  const handleConnect = async () => {
    if (!credentials.username || !credentials.password) {
      toast.error('Введите логин и пароль');
      return;
    }

    setConnecting(true);
    try {
      const response = await axios.post(`${API}/api/gelios/connect`, credentials, getAuthHeaders());
      toast.success(response.data.message);
      fetchSettings();
      // Auto-load units after connecting
      setTimeout(() => fetchUnits(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка подключения');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/api/gelios/disconnect`, {}, getAuthHeaders());
      toast.success('Отключено от Gelios');
      setSettings(prev => ({ ...prev, connected: false }));
      setUnits([]);
    } catch (error) {
      toast.error('Ошибка отключения');
    }
  };

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/gelios/units`, getAuthHeaders());
      setUnits(response.data.units);
      
      // Load existing mappings
      const mappings = {};
      for (const unit of response.data.units) {
        if (unit.mapped_bus_id) {
          mappings[unit.id] = unit.mapped_bus_id;
        }
      }
      
      // Also check buses for gelios_unit_id
      for (const bus of buses) {
        if (bus.gelios_unit_id) {
          mappings[bus.gelios_unit_id] = bus.id;
        }
      }
      
      setSelectedMappings(mappings);
      
      if (response.data.note) {
        toast.info(response.data.note, { duration: 5000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkBus = async (unit, busId) => {
    if (!busId) {
      // Unlink
      try {
        const currentBusId = selectedMappings[unit.id];
        if (currentBusId) {
          await axios.post(`${API}/api/gelios/unlink-bus`, { bus_id: currentBusId }, getAuthHeaders());
          setSelectedMappings(prev => {
            const newMappings = { ...prev };
            delete newMappings[unit.id];
            return newMappings;
          });
          toast.success('Привязка удалена');
          fetchBuses();
        }
      } catch (error) {
        toast.error('Ошибка удаления привязки');
      }
      return;
    }

    setSyncing(true);
    try {
      await axios.post(`${API}/api/gelios/link-bus`, {
        gelios_unit_id: unit.id,
        gelios_unit_name: unit.name,
        bus_id: busId
      }, getAuthHeaders());
      
      setSelectedMappings(prev => ({ ...prev, [unit.id]: busId }));
      toast.success('Объект Gelios привязан к автобусу');
      fetchBuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка привязки');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleString('ru-RU');
  };

  const formatOdometer = (value) => {
    if (!value && value !== 0) return '—';
    return `${Math.round(value).toLocaleString('ru-RU')} км`;
  };

  const handleOpenOdometerEdit = (unit, busId) => {
    const bus = buses.find(b => b.id === busId);
    setEditingOdometer({
      unitId: unit.id,
      unitName: unit.name,
      busId: busId,
      busName: bus ? `${bus.license_plate}` : '',
      currentValue: unit.gelios_odometer || 0
    });
    setOdometerValue(unit.gelios_odometer?.toString() || '');
  };

  const handleSaveOdometer = async () => {
    if (!editingOdometer || !odometerValue) return;
    
    const odometer = parseInt(odometerValue, 10);
    if (isNaN(odometer) || odometer < 0) {
      toast.error('Введите корректное значение пробега');
      return;
    }

    setSyncing(true);
    try {
      // Save to gelios_units collection
      await axios.post(`${API}/api/gelios/update-odometer`, {
        unit_id: editingOdometer.unitId,
        unit_name: editingOdometer.unitName,
        odometer: odometer
      }, getAuthHeaders());
      
      toast.success(`Пробег Gelios ${formatOdometer(odometer)} сохранён`);
      setEditingOdometer(null);
      setOdometerValue('');
      fetchUnits(); // Refresh to show updated odometer
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения пробега');
    } finally {
      setSyncing(false);
    }
  };

  // Get bus that's already linked to check for duplicates
  const getLinkedBusIds = () => {
    const linked = new Set();
    for (const busId of Object.values(selectedMappings)) {
      linked.add(busId);
    }
    return linked;
  };

  const linkedBusIds = getLinkedBusIds();

  return (
    <div className="space-y-4">
      {/* Connection Card */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Truck className="w-5 h-5" />
            Интеграция с Gelios Pro
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings?.connected ? (
            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Check className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Подключено</p>
                  <p className="text-sm text-green-600">
                    Пользователь: {settings.username} • Объектов: {settings.units_count}
                  </p>
                  {settings.last_sync && (
                    <p className="text-xs text-green-500">
                      Последняя синхронизация: {formatDate(settings.last_sync)}
                    </p>
                  )}
                </div>
              </div>
              <Button variant="outline" onClick={handleDisconnect} className="gap-2">
                <Unlink className="w-4 h-4" />
                Отключить
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Не подключено к Gelios</span>
                </div>
                <p className="text-sm text-amber-600 mt-1">
                  Введите данные для подключения к eu.geliospro.net
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Логин Gelios</Label>
                  <Input
                    value={credentials.username}
                    onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Введите логин"
                    data-testid="gelios-username"
                  />
                </div>
                <div>
                  <Label>Пароль Gelios</Label>
                  <Input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Введите пароль"
                    data-testid="gelios-password"
                  />
                </div>
              </div>

              <Button onClick={handleConnect} disabled={connecting} className="gap-2" data-testid="gelios-connect-btn">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Подключиться
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Units List */}
      {settings?.connected && (
        <Card>
          <CardHeader className="py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Gauge className="w-5 h-5" />
                Объекты Gelios ({units.length})
              </CardTitle>
              <Button variant="outline" onClick={fetchUnits} disabled={loading} className="gap-2" data-testid="gelios-refresh-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Обновить
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {units.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Нажмите «Обновить» для загрузки данных</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Info banner */}
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-green-700">
                    <p className="font-medium">Данные Gelios загружены в реальном времени</p>
                    <p className="text-green-600">
                      Отображаются: пробег, одометр, топливо, моточасы, скорость и другие параметры.
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Название</th>
                        <th className="px-3 py-2 text-center">Одометр</th>
                        <th className="px-3 py-2 text-center">Пробег GPS</th>
                        <th className="px-3 py-2 text-center">Топливо</th>
                        <th className="px-3 py-2 text-center">Моточасы</th>
                        <th className="px-3 py-2 text-center">Скорость</th>
                        <th className="px-3 py-2 text-center">Обновлено</th>
                        <th className="px-3 py-2 text-left">Автобус BUSAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((unit, idx) => {
                        const mappedBusId = selectedMappings[unit.id];
                        const mappedBus = buses.find(b => b.id === mappedBusId);
                        const hasData = unit.odometer || unit.mileage || unit.fuel;
                        
                        return (
                          <tr key={unit.id} className={`border-t ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${!hasData ? 'opacity-60' : ''}`}>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${unit.ignition ? 'bg-green-500' : 'bg-gray-300'}`} title={unit.ignition ? 'Зажигание вкл' : 'Зажигание выкл'} />
                                <Truck className="w-4 h-4 text-blue-500" />
                                <span className="font-medium">{unit.name}</span>
                              </div>
                              <span className="text-xs text-gray-400 ml-8">ID: {unit.id}</span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              {unit.odometer ? (
                                <span className="font-medium text-blue-600 flex items-center justify-center gap-1">
                                  <Gauge className="w-4 h-4" />
                                  {formatOdometer(unit.odometer)}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center text-gray-600">
                              {unit.mileage ? formatOdometer(unit.mileage) : '—'}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {unit.fuel ? (
                                <span className="inline-flex items-center gap-1 text-amber-600">
                                  <Fuel className="w-4 h-4" />
                                  {unit.fuel} л
                                </span>
                              ) : '—'}
                              {unit.adblue !== null && unit.adblue !== undefined && (
                                <div className="text-xs text-blue-500">AdBlue: {unit.adblue}%</div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center text-gray-600">
                              {unit.motohours ? `${unit.motohours.toFixed(1)} ч` : '—'}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {unit.speed !== null && unit.speed !== undefined ? (
                                <span className={unit.speed > 0 ? 'text-green-600 font-medium' : 'text-gray-500'}>
                                  {unit.speed} км/ч
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-3 text-center text-xs text-gray-500">
                              {unit.last_update || '—'}
                            </td>
                            <td className="px-3 py-3">
                              <Select
                                value={mappedBusId || 'none'}
                                onValueChange={(val) => handleLinkBus(unit, val === 'none' ? '' : val)}
                                disabled={syncing}
                              >
                                <SelectTrigger className="w-48" data-testid={`gelios-bus-select-${unit.id}`}>
                                  <SelectValue placeholder="Выберите" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— Не привязан —</SelectItem>
                                  {buses.map(bus => {
                                    const isLinked = linkedBusIds.has(bus.id) && bus.id !== mappedBusId;
                                    return (
                                      <SelectItem 
                                        key={bus.id} 
                                        value={bus.id}
                                        disabled={isLinked}
                                      >
                                        {bus.license_plate}
                                        {isLinked && ' ✓'}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card>
        <CardContent className="py-4">
          <div className="text-sm text-gray-500 space-y-1">
            <p><strong>Данные из Gelios Pro:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Одометр</strong> — показания с CAN-шины автомобиля</li>
              <li><strong>Пробег GPS</strong> — рассчитанный по GPS треку</li>
              <li><strong>Топливо</strong> — уровень топлива с датчика</li>
              <li><strong>Моточасы</strong> — время работы двигателя</li>
              <li><strong>Скорость</strong> — текущая скорость (0 = стоит)</li>
            </ul>
            <p className="mt-3 text-green-600">
              <strong>✓</strong> Данные обновляются при каждом нажатии «Обновить»
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
